<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import type { AnimalMedicalCosts, MedicalCost } from '../../../../types';
import { useAToast } from '@Atoms';
import { scrollToTop, toCents } from '@Helpers';
import { useDataTableFilters } from '@DataTableModule';
import medicalCostsTableHelper from './medicalCostsTableHelper';
import useCartStore from '../../../../store';
import useDataTable from './useDataTable';
import { CART_STEPS } from '../../../../types';
import {
  CalculateCartMedicalCostsService,
  UpdateMedicalCostAddToReceiptService,
  UpdateMedicalCostOnCartService,
  UpdateMedicalCostRetailCostService,
} from '../../../../services';
import { MEDICAL_COST_TYPES } from '../../../../enums';
import { useCartSteps } from '../../../../composables';
import { MEDICAL_COLLECTIONS } from '../../../../constants';

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const { currentStep, previousStep, nextStep } = useCartSteps();
const { loading, cartData, animals } = storeToRefs(useCartStore());

const { notify, TOAST_TYPES } = useAToast();
const { push } = useRouter();
const { t } = useI18n();
const dataTableInstance = useDataTable();
const { activeColumns, columns, initialColumns, rows, searchQuery, selectedType } = dataTableInstance;
const { selectFilter } = useDataTableFilters();

const typeFilterOptions = computed(() => [
  { label: t('cart.medicalCosts.types.all'), value: 'all' },
  { label: t('cart.medicalCosts.types.medications'), value: 'medications' },
  { label: t('cart.medicalCosts.types.vaccinations'), value: 'vaccinations' },
  { label: t('cart.medicalCosts.types.procedures'), value: 'procedures' },
  { label: t('cart.medicalCosts.types.diagnostics'), value: 'diagnostics' },
  { label: t('cart.medicalCosts.types.surgeries'), value: 'surgeries' },
  { label: t('cart.medicalCosts.types.exams'), value: 'exams' },
]);

const medicalCostsLoading = ref(true);
const medicalCostsActiveStep = ref(0);

const animalsCartLength = computed(() => animals.value.length);
const currentAnimal = computed(() => animals.value[medicalCostsActiveStep.value]);
const nextStepButton = computed(() => ({
  icon: nextStep.value.step ? nextStep.value.step.header.icon : '',
}));
const hasMedicalCosts = computed(() => {
  const animal = currentAnimal.value;
  if (!animal?.medical_costs) return false;

  return Object.values(animal.medical_costs).some((costs: any) => Array.isArray(costs) && costs.length > 0);
});

onMounted(() => onStepMount());

watch(medicalCostsActiveStep, onAnimalStepChange);
watch(searchQuery, () => (rows.value = setTableRows()));
watch(selectedType, () => (rows.value = setTableRows()));

// HELPERS
function granularUpdate(newData: any, updatedItemId: string, updatedItemType: string) {
  const updatedAnimal = newData.animals?.find((animal: any) => animal.animal_id === currentAnimal.value.animal_id);
  if (!updatedAnimal) return;

  const currentAnimalIndex = animals.value.findIndex((animal: any) => animal.animal_id === updatedAnimal.animal_id);
  if (currentAnimalIndex === -1) return;

  const targetAnimal = animals.value[currentAnimalIndex];

  // NOTE :: Update retail cost or add to receipt for the specific medical cost item
  const targetFieldToUpdate = updatedAnimal.medical_costs?.[updatedItemType];
  if (targetFieldToUpdate) targetAnimal.medical_costs[updatedItemType] = targetFieldToUpdate;

  // NOTE :: Update animal-level flat maps and totals
  const animalKeysToUpdate = [
    'add_to_receipt_edits',
    'medical_total',
    'original_retail_costs',
    'retail_cost_edits',
    'retail_cost_total',
    'total',
  ];
  animalKeysToUpdate.forEach((key) => updatedAnimal[key] !== undefined && (targetAnimal[key] = updatedAnimal[key]));

  // NOTE :: Update cart-level totals
  const cartKeysToUpdate = [
    'animal_subtotal',
    'animal_tax',
    'animal_total',
    'cc_fee',
    'medical_total',
    'processing_donation',
    'retail_cost_total',
    'subtotal',
    'tax',
    'total',
    'total_charge',
  ];
  cartKeysToUpdate.forEach((key) => newData[key] !== undefined && (cartData.value[key] = newData[key]));

  // NOTE :: Update the specific row in the table
  const rowIndex = rows.value.findIndex((row) => row._id === updatedItemId);
  if (rowIndex === -1) return;

  const updatedCost = targetFieldToUpdate?.find((cost: any) => cost._id === updatedItemId);
  if (!updatedCost) return;

  Object.assign(rows.value[rowIndex], updatedCost);
}

function setTableRows() {
  const activeAnimal = animals.value[medicalCostsActiveStep.value];
  if (!activeAnimal?.medical_costs) return [];

  const animalMedicalCosts: AnimalMedicalCosts = activeAnimal.medical_costs;

  const medicalCosts: MedicalCost[] = [];
  const searchTerm = (searchQuery.value ?? '').toLowerCase().trim();
  const filterType = selectedType.value;

  MEDICAL_COLLECTIONS.forEach((collection) => {
    if (filterType !== 'all' && collection !== filterType) return;

    const costs = animalMedicalCosts[collection];
    if (!costs?.length) return;

    costs.forEach((cost, index) => {
      if (searchTerm) {
        const costId = cost.id?.toString().toLowerCase() || '';
        const itemName = cost.item?.toLowerCase() || '';
        const matchesSearch = costId.includes(searchTerm) || itemName.includes(searchTerm);
        if (!matchesSearch) return;
      }

      const label = index === 0 ? collection : '';
      const costWithOriginal: MedicalCost = { ...cost, label, type: collection };

      medicalCosts.push(costWithOriginal);
    });
  });

  return medicalCosts;
}

// EVENTS
function onTypeFilterUpdate() {
  const params = new URLSearchParams(selectFilter.value?.replace('&', '') || '');
  selectedType.value = params.get('type') || 'all';
}

async function onRefreshMedicalCosts() {
  loading.value = true;
  medicalCostsLoading.value = true;

  await fetchMedicalCosts().finally(() => {
    loading.value = false;
    medicalCostsLoading.value = false;
  });
}

function onAnimalStepChange() {
  scrollToTop();
  fetchMedicalCosts();
}

async function fetchMedicalCosts() {
  medicalCostsLoading.value = true;

  const TOAST_ERROR = {
    message: 'cart.toast.medicalCosts.error.message',
    title: 'cart.toast.medicalCosts.error.title',
    type: TOAST_TYPES.ERROR,
  };

  const currentAnimalId = currentAnimal.value?.animal_id;
  if (!currentAnimalId) {
    medicalCostsLoading.value = false;
    return;
  }

  const PAYLOAD = {
    animal_id: currentAnimalId,
  };

  await CalculateCartMedicalCostsService(PAYLOAD)
    .then((cart: any) => onFreshMedicalCosts(cart))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (medicalCostsLoading.value = false));
}

async function onStepMount() {
  medicalCostsActiveStep.value = 0;
  currentStep.value = CART_STEPS.MEDICAL_COSTS;
  selectFilter.value = '';

  await fetchMedicalCosts();
}

function onNextAnimal(action: Function) {
  scrollToTop();
  action();
}

function onFreshMedicalCosts(cart: any) {
  cartData.value = cart;

  const staticColumns = [
    {
      name: 'id',
      label: 'ID',
    },
    {
      name: 'item',
      label: 'Item',
    },
    {
      name: 'start_date',
      label: 'Start Date',
    },
    {
      name: 'status',
      label: 'Status',
    },
    {
      name: 'retail_cost',
      label: 'Retail Cost',
    },
  ];

  columns.value = staticColumns.map((field) => medicalCostsTableHelper(field));
  initialColumns.value = columns.value.map(({ field }) => field);
  activeColumns.value = initialColumns.value;
  rows.value = setTableRows();
}

function onRetailCostUpdate(newValue: any, originalData: any, fieldName: string) {
  if (fieldName !== 'retail_cost') return;

  let retailCost = typeof newValue === 'number' ? newValue : parseFloat(newValue) || 0;

  /* NOTE
   * For vaccinations, the retail_cost shown in cart is the total (per dose * dose_count)
   * We need to divide by dose_count to get the per-dose cost to save in the database
   * Using toCents() to avoid floating point precision issues
   */
  const vacinationsWithDoseCount =
    originalData.type === MEDICAL_COST_TYPES.VACCINATIONS && originalData.dose_count && originalData.dose_count > 0;
  if (vacinationsWithDoseCount) {
    retailCost = toCents(retailCost) / originalData.dose_count / 100;
  }

  medicalCostsLoading.value = true;

  const TOAST_ERROR = {
    message: 'cart.toast.addItem.error.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  const PAYLOAD = {
    _id: originalData._id,
    animal_id: originalData.animal_id,
    type: originalData.type,
    retail_cost: retailCost,
  };

  UpdateMedicalCostRetailCostService(PAYLOAD)
    .then((data) => granularUpdate(data, originalData._id, originalData.type))
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (medicalCostsLoading.value = false));
}

function onUpdateMedicalCostOnCart(props: MedicalCost, newValue: boolean) {
  const TOAST_ERROR = {
    message: 'cart.toast.addItem.error.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  const PAYLOAD = {
    _id: props._id,
    animal_id: props.animal_id,
    type: props.type,
    add_to_receipt: newValue,
  };

  UpdateMedicalCostOnCartService(PAYLOAD)
    .then(({ data }) => granularUpdate(data, props._id, props.type))
    .catch(() => notify(TOAST_ERROR));
}

function onNextStep() {
  const TOAST_ERROR = {
    message: 'Something went wrong while updating the medical costs add to receipt.',
    title: 'Refresh the page and try again. If the issue persists, please contact support.',
    type: TOAST_TYPES.ERROR,
  };

  const receiptHasMedicalCosts = animals.value.some((animal: any) => {
    if (!animal?.medical_costs) return false;

    return MEDICAL_COLLECTIONS.some((collection) => {
      const costs = animal.medical_costs?.[collection];
      if (!costs?.length) return false;

      return costs.some((cost: MedicalCost) => {
        // NOTE :: add_to_receipt takes precedence over add_to_total
        if (cost.add_to_receipt !== undefined) {
          return cost.add_to_receipt === true;
        }
        return cost.add_to_total === true;
      });
    });
  });

  medicalCostsLoading.value = true;

  UpdateMedicalCostAddToReceiptService({ receipt_has_medical_costs: receiptHasMedicalCosts })
    .then((data) => {
      cartData.value = data;
      emits('onStepUpdate', nextStep.value.step!.name);
    })
    .catch(() => notify(TOAST_ERROR))
    .finally(() => (medicalCostsLoading.value = false));
}

function onNavigateToAnimalMedical() {
  const animal = currentAnimal.value;
  if (!animal?._id) return;

  push({ name: 'animal-profile-medical', params: { id: animal._id } });
}
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <Stepper
      v-model:activeStep="medicalCostsActiveStep"
      linear
      pt:header="px-0 flex-[0]"
      pt:nav="justify-start"
      pt:panelContainer="px-0 pb-0"
      pt:stepper="h-full"
    >
      <StepperPanel
        v-for="animal in animals"
        :key="animal._id"
      >
        <template #header="{ index, clickCallback }">
          <div class="flex w-full items-center justify-between gap-2">
            <div class="flex items-center gap-2">
              <Button
                rounded
                class="grid !h-8 !w-5 place-content-center border-none bg-brand-500 text-sm"
                :label="(index + 1).toString()"
                @click="clickCallback"
              />

              <Tag
                severity="warning"
                :value="animal.animal_name"
                @click="clickCallback"
              />
            </div>
          </div>
        </template>

        <template #content="{ index, nextCallback, prevCallback }">
          <div class="scrollToTop flex h-full min-h-0 flex-col gap-4">
            <template v-if="hasMedicalCosts">
              <header
                ref="tablePageHeader"
                class="flex-shrink-0"
              >
                <FiltersTemplate
                  class="!my-0"
                  :loading
                  :hasDataTableFilters="false"
                  :hasFilters="false"
                  :instanceComposable="useDataTable"
                >
                  <Button
                    v-tooltip.top="t('cart.medicalCosts.refreshButton.tooltip')"
                    hasBorder
                    outlined
                    class="af-filter-button"
                    icon="pi pi-refresh"
                    :loading
                    @click="onRefreshMedicalCosts"
                  />

                  <ASelectFilter
                    hasClearTrigger
                    defaultValue="all"
                    inputLabel="filters.type.title"
                    paramsLabel="type"
                    optionValue="value"
                    :loading
                    :options="typeFilterOptions"
                    @onParamsUpdate="onTypeFilterUpdate"
                  />
                </FiltersTemplate>
              </header>

              <div class="flex min-h-0 flex-1 overflow-y-auto pb-4 pr-2">
                <MDataTable
                  class="w-full"
                  customEmptyStateHeight="500px"
                  customLoadingHeight="500px"
                  noPreDefinedFrozenColumns
                  :loading="loading || medicalCostsLoading"
                  :hasAsideColumn="false"
                  :hasSelector="false"
                  :hasPaginator="false"
                  :tunnelProps="{ header: 'Add to Receipt' }"
                  :instanceComposable="useDataTable"
                  @onFieldUpdate="onRetailCostUpdate"
                  @onParamsUpdate="fetchMedicalCosts"
                >
                  <template #tunnel="{ props }">
                    <div class="flex w-full items-center justify-center">
                      <Checkbox
                        binary
                        :modelValue="props.add_to_receipt ?? props.add_to_total ?? false"
                        @update:modelValue="(value: boolean) => onUpdateMedicalCostOnCart(props, value)"
                      />
                    </div>
                  </template>
                </MDataTable>
              </div>
            </template>

            <MTableEmptyState
              v-else
              icon="faStethoscope"
              message="cart.medicalCosts.emptyState.message"
            >
              <template #button>
                <Button
                  class="af-button af-button-success"
                  icon="pi pi-external-link"
                  :label="$t('cart.medicalCosts.emptyState.button')"
                  @click="onNavigateToAnimalMedical"
                />
              </template>
            </MTableEmptyState>

            <div class="flex flex-shrink-0 border-t bg-white pb-0 pt-4">
              <Button
                v-if="previousStep"
                outlined
                class="af-button af-button-success-outlined"
                icon="pi pi-arrow-left"
                :label="$t('common.back')"
                :loading="medicalCostsLoading"
                @click="emits('onStepUpdate', previousStep)"
              />

              <div class="ml-auto flex gap-2">
                <template v-if="animalsCartLength > 1">
                  <Button
                    v-if="index > 0"
                    outlined
                    class="af-button af-button-success-outlined"
                    icon="pi pi-arrow-left"
                    :label="$t('common.previous')"
                    :loading="medicalCostsLoading"
                    @click="onNextAnimal(prevCallback)"
                  />

                  <Button
                    v-if="index + 1 < animalsCartLength"
                    outlined
                    class="af-button af-button-success-outlined"
                    icon="pi pi-arrow-right"
                    :label="$t('common.next')"
                    :loading="medicalCostsLoading"
                    @click="onNextAnimal(nextCallback)"
                  />

                  <Button
                    v-if="index + 1 >= animalsCartLength"
                    class="af-button af-button-success"
                    iconPos="right"
                    :label="$t('common.nextStep')"
                    :loading="medicalCostsLoading"
                    @click="onNextStep"
                  >
                    <template #icon>
                      <FaIcon
                        v-if="nextStepButton.icon"
                        class="mr-2"
                        :icon="$icons[nextStepButton.icon]"
                      />
                    </template>
                  </Button>
                </template>

                <Button
                  v-else
                  class="af-button af-button-success"
                  iconPos="right"
                  :label="$t('common.nextStep')"
                  :loading="medicalCostsLoading"
                  @click="onNextStep"
                >
                  <template #icon>
                    <FaIcon
                      v-if="nextStepButton.icon"
                      class="mr-2"
                      :icon="$icons[nextStepButton.icon]"
                    />
                  </template>
                </Button>
              </div>
            </div>
          </div>
        </template>
      </StepperPanel>
    </Stepper>
  </div>
</template>
